import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  useTheme,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Alert,
  Snackbar,
  Stack,
  Chip,
} from '@mui/material';
import { 
  Phone, 
  Email, 
  LocationOn, 
  Send, 
  CheckCircle,
  Business,
  Schedule,
} from '@mui/icons-material';
import { getCompanyInfo, submitContactForm, submitQuoteRequest, CompanyInfo } from '../api/publicApi';
import { BRAND } from '../config/brand';
import { BrandHero } from '../components/brand/BrandHero';

interface ContactFormData {
  name: string;
  email: string;
  phone?: string;
  company?: string;
  message: string;
  service?: string;
}

interface QuoteRequestData {
  name: string;
  email: string;
  phone: string;
  company: string;
  service: string;
  description: string;
  quantity?: number;
  timeline?: string;
  budget?: string;
}

const services = [
  'Custom Textile Manufacturing',
  'Smart Furniture Production',
  'Precision Metalworks',
  'Supply Chain Solutions',
  'Quality Assurance Services',
  'Custom Manufacturing',
  'Other',
];

export const Contact: React.FC = () => {
  const theme = useTheme();
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [activeTab, setActiveTab] = useState<'contact' | 'quote'>('contact');
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const [contactForm, setContactForm] = useState<ContactFormData>({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
    service: '',
  });

  const [quoteForm, setQuoteForm] = useState<QuoteRequestData>({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    description: '',
    quantity: undefined,
    timeline: '',
    budget: '',
  });

  useEffect(() => {
    const fetchCompanyInfo = async () => {
      try {
        const info = await getCompanyInfo();
        setCompanyInfo(info);
      } catch (error) {
        console.error('Error fetching company info:', error);
      }
    };

    fetchCompanyInfo();
  }, []);

  const handleContactChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setContactForm({
      ...contactForm,
      [e.target.name]: e.target.value,
    });
  };

  const handleQuoteChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setQuoteForm({
      ...quoteForm,
      [e.target.name]: e.target.value,
    });
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await submitContactForm(contactForm);
      setSnackbar({
        open: true,
        message: 'Thank you for your message! We will get back to you soon.',
        severity: 'success',
      });
      setContactForm({
        name: '',
        email: '',
        phone: '',
        company: '',
        message: '',
        service: '',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to send message. Please try again.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleQuoteSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await submitQuoteRequest(quoteForm);
      setSnackbar({
        open: true,
        message: 'Your quote request has been submitted! We will review and get back to you within 24 hours.',
        severity: 'success',
      });
      setQuoteForm({
        name: '',
        email: '',
        phone: '',
        company: '',
        service: '',
        description: '',
        quantity: undefined,
        timeline: '',
        budget: '',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to submit quote request. Please try again.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: BRAND.colors.creamLight }}>
      <BrandHero
        title="Contact Us"
        subtitle={BRAND.tagline}
        description="Get in touch with Monio Cluster Manufacturers — questions, quotes, and partnerships welcome."
        backgroundImage={BRAND.images.teamFacility}
        align="center"
      />

      {/* Contact Forms */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 700 }}>
            How Can We Help You?
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
            Choose how you'd like to get in touch with us
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Chip
              label="General Inquiry"
              onClick={() => setActiveTab('contact')}
              sx={{
                background: activeTab === 'contact' 
                  ? 'linear-gradient(135deg, #1b4d89 0%, #0f3460 100%)'
                  : 'rgba(255, 255, 255, 0.1)',
                color: activeTab === 'contact' ? 'white' : 'text.primary',
                border: activeTab === 'contact' 
                  ? 'none' 
                  : '1px solid rgba(99, 102, 241, 0.2)',
                '&:hover': {
                  background: activeTab === 'contact' 
                    ? 'linear-gradient(135deg, #0f3460 0%, #1b4d89 100%)'
                    : 'rgba(99, 102, 241, 0.1)',
                },
              }}
            />
            <Chip
              label="Request Quote"
              onClick={() => setActiveTab('quote')}
              sx={{
                background: activeTab === 'quote' 
                  ? 'linear-gradient(135deg, #1b4d89 0%, #0f3460 100%)'
                  : 'rgba(255, 255, 255, 0.1)',
                color: activeTab === 'quote' ? 'white' : 'text.primary',
                border: activeTab === 'quote' 
                  ? 'none' 
                  : '1px solid rgba(99, 102, 241, 0.2)',
                '&:hover': {
                  background: activeTab === 'quote' 
                    ? 'linear-gradient(135deg, #0f3460 0%, #1b4d89 100%)'
                    : 'rgba(99, 102, 241, 0.1)',
                },
              }}
            />
          </Stack>
        </Box>

        <Grid container spacing={6}>
          {/* Contact Form */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: '100%',
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: 3,
                boxShadow: '0px 20px 25px rgba(0, 0, 0, 0.1), 0px 10px 10px rgba(0, 0, 0, 0.04)',
                p: 4,
              }}
            >
              <Box sx={{ mb: 4 }}>
                <Typography variant="h4" gutterBottom sx={{
                  background: 'linear-gradient(135deg, #1b4d89 0%, #0f3460 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontWeight: 600,
                }}>
                  {activeTab === 'contact' ? 'Send Us a Message' : 'Request a Quote'}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  {activeTab === 'contact' 
                    ? 'Get in touch with us using the form below or through our contact information.'
                    : 'Tell us about your project and we\'ll provide you with a custom quote.'
                  }
                </Typography>
              </Box>

              {activeTab === 'contact' ? (
                <form onSubmit={handleContactSubmit}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Full Name"
                        name="name"
                        value={contactForm.name}
                        onChange={handleContactChange}
                        required
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Email"
                        name="email"
                        value={contactForm.email}
                        onChange={handleContactChange}
                        required
                        type="email"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Phone (Optional)"
                        name="phone"
                        value={contactForm.phone}
                        onChange={handleContactChange}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Company (Optional)"
                        name="company"
                        value={contactForm.company}
                        onChange={handleContactChange}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Service of Interest (Optional)"
                        name="service"
                        value={contactForm.service}
                        onChange={handleContactChange}
                        select
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          },
                        }}
                      >
                        {services.map((service) => (
                          <MenuItem key={service} value={service}>
                            {service}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Message"
                        name="message"
                        multiline
                        rows={4}
                        value={contactForm.message}
                        onChange={handleContactChange}
                        required
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        fullWidth
                        variant="contained"
                        size="large"
                        type="submit"
                        disabled={loading}
                        startIcon={loading ? <Schedule /> : <Send />}
                        sx={{
                          py: 2,
                          px: 4,
                          borderRadius: 2,
                          background: 'linear-gradient(135deg, #1b4d89 0%, #0f3460 100%)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #0f3460 0%, #1b4d89 100%)',
                          },
                        }}
                      >
                        {loading ? 'Sending...' : 'Send Message'}
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              ) : (
                <form onSubmit={handleQuoteSubmit}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Full Name"
                        name="name"
                        value={quoteForm.name}
                        onChange={handleQuoteChange}
                        required
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Email"
                        name="email"
                        value={quoteForm.email}
                        onChange={handleQuoteChange}
                        required
                        type="email"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Phone"
                        name="phone"
                        value={quoteForm.phone}
                        onChange={handleQuoteChange}
                        required
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Company"
                        name="company"
                        value={quoteForm.company}
                        onChange={handleQuoteChange}
                        required
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Service Required"
                        name="service"
                        value={quoteForm.service}
                        onChange={handleQuoteChange}
                        select
                        required
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          },
                        }}
                      >
                        {services.map((service) => (
                          <MenuItem key={service} value={service}>
                            {service}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Project Description"
                        name="description"
                        value={quoteForm.description}
                        onChange={handleQuoteChange}
                        multiline
                        rows={4}
                        required
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Quantity (Optional)"
                        name="quantity"
                        value={quoteForm.quantity}
                        onChange={handleQuoteChange}
                        type="number"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Timeline (Optional)"
                        name="timeline"
                        value={quoteForm.timeline}
                        onChange={handleQuoteChange}
                        placeholder="e.g., 2-4 weeks"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Budget Range (Optional)"
                        name="budget"
                        value={quoteForm.budget}
                        onChange={handleQuoteChange}
                        placeholder="e.g., $10,000 - $50,000"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        fullWidth
                        variant="contained"
                        size="large"
                        type="submit"
                        disabled={loading}
                        startIcon={loading ? <Schedule /> : <Send />}
                        sx={{
                          py: 2,
                          px: 4,
                          borderRadius: 2,
                          background: 'linear-gradient(135deg, #1b4d89 0%, #0f3460 100%)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #0f3460 0%, #1b4d89 100%)',
                          },
                        }}
                      >
                        {loading ? 'Submitting...' : 'Request Quote'}
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              )}
            </Card>
          </Grid>

          {/* Contact Information */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: '100%',
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: 3,
                boxShadow: '0px 20px 25px rgba(0, 0, 0, 0.1), 0px 10px 10px rgba(0, 0, 0, 0.04)',
                p: 4,
              }}
            >
              <Typography variant="h4" gutterBottom sx={{
                background: 'linear-gradient(135deg, #1b4d89 0%, #0f3460 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontWeight: 600,
                mb: 4,
              }}>
                Get in Touch
              </Typography>

              <List>
                <ListItem sx={{ px: 0, mb: 3 }}>
                  <ListItemIcon>
                    <Phone sx={{ color: '#6366f1', fontSize: 30 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Phone"
                    secondary={companyInfo?.contact?.phone || '+254 700 000 000'}
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                </ListItem>
                <ListItem sx={{ px: 0, mb: 3 }}>
                  <ListItemIcon>
                    <Email sx={{ color: '#06b6d4', fontSize: 30 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Email"
                    secondary={companyInfo?.contact?.email || 'info@monio.com'}
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                </ListItem>
                <ListItem sx={{ px: 0, mb: 3 }}>
                  <ListItemIcon>
                    <LocationOn sx={{ color: '#10b981', fontSize: 30 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Address"
                    secondary={companyInfo?.contact?.address || 'Monio Headquarters, Nairobi, Kenya'}
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                </ListItem>
                <ListItem sx={{ px: 0, mb: 3 }}>
                  <ListItemIcon>
                    <Business sx={{ color: '#f59e0b', fontSize: 30 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Business Hours"
                    secondary="Monday - Friday: 8:00 AM - 6:00 PM"
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                </ListItem>
              </List>

              <Box sx={{ mt: 4, p: 3, background: 'rgba(99, 102, 241, 0.1)', borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#6366f1' }}>
                  Why Choose Monio?
                </Typography>
                <List dense>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon>
                      <CheckCircle sx={{ color: '#10b981', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary="Expert manufacturing solutions" />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon>
                      <CheckCircle sx={{ color: '#10b981', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary="Quality assurance guaranteed" />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon>
                      <CheckCircle sx={{ color: '#10b981', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary="Fast response times" />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon>
                      <CheckCircle sx={{ color: '#10b981', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary="Competitive pricing" />
                  </ListItem>
                </List>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
